import React from "react";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import LeadForm from "@/components/LeadForm";
import PshlWordmark from "@/components/PshlWordmark";
import { Download, Image as ImageIcon, FileText, Mail, Video, Link2 } from "lucide-react";
import { BRAND } from "@/data/brand";
import { toast } from "sonner";

const VARIANTS = [
  {
    id: "primary",
    label: "Primary",
    desc: "Silver-chrome PSHL + metallic-gold ∞. Use on dark backgrounds — broadcast lower-thirds, app UI, hero imagery, dark jerseys.",
    bgClass: "bg-[#020A1A]",
    swatch: "Dark",
    file: "/pshl8-wordmark.svg",
    component: <PshlWordmark variant="primary" className="h-14 md:h-16 w-auto" />,
  },
  {
    id: "dark",
    label: "Dark on Light",
    desc: "Graphite PSHL + solid gold ∞. Use on light/white backgrounds — print collateral, sponsor decks, light merchandise.",
    bgClass: "bg-[#F4F4F5]",
    swatch: "Light",
    file: "/pshl8-wordmark-dark.svg",
    component: <PshlWordmark variant="dark" className="h-14 md:h-16 w-auto" />,
  },
  {
    id: "mono",
    label: "Monochrome",
    desc: "Single-color silhouette (uses currentColor). Use for embossing, foil-stamp, one-color print, social avatars, or any constrained-color application.",
    bgClass: "bg-pshl-electric",
    swatch: "Mono",
    file: "/pshl8-wordmark-mono.svg",
    component: <PshlWordmark variant="mono" color="#FFFFFF" className="h-14 md:h-16 w-auto" />,
  },
];

const ASSETS = [
  { Icon: FileText,  title: "Press Kit",        desc: "Official press kit — league overview, brand story, fact sheet." },
  { Icon: Video,     title: "Match Highlights", desc: "Curated reels and highlight clips for broadcasters and editors." },
  { Icon: ImageIcon, title: "Gallery",          desc: "High-resolution match-day, fan-zone, and behind-the-scenes imagery." },
  { Icon: FileText,  title: "Brand Guidelines", desc: "Typography, color tokens, clear-space rules, do's and don'ts." },
  { Icon: Mail,      title: "Media Contact",    desc: `Pitches, interviews, accreditations — ${BRAND.email}` },
];

export default function Media() {
  return (
    <div>
      <PageMeta
        title="Media Centre — PSHL8 Press Kit"
        description="Everything broadcasters, publishers and creators need to cover PSHL8. Logos, brand pack, fact sheet, photos and press contact."
        image="/api/og/media/og-image.png"
      />
      <PageHero
        overline="Media Centre"
        title={<>Press Kit. Brand. <span className="text-pshl-electric">Storytelling.</span></>}
        subtitle="Everything broadcasters, publishers and creators need to cover PSHL8."
        bgImage="https://images.pexels.com/photos/8972990/pexels-photo-8972990.jpeg"
      />

      {/* MASTER BRAND MARK — the official PSHL∞ identity */}
      <section data-testid="section-master-brand-mark" className="section pb-0">
        <div className="pshl-container space-y-6">
          {/* Hero poster artwork — the full broadcast keyart with player + 8 pillars */}
          <div data-testid="brand-hero-poster" className="bg-pshl-surface border border-pshl-gold/30 p-6 md:p-10 overflow-hidden relative">
            <div className="absolute -inset-16 bg-gradient-to-br from-pshl-red/12 via-transparent to-pshl-electric/12 blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <div className="text-xs uppercase tracking-[0.32em] text-pshl-gold font-display mb-3">Hero Keyart · Broadcast</div>
                <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter leading-[0.95]">
                  PSHL<span className="text-gold-metallic">∞</span> Broadcast Poster.
                </h2>
                <p className="mt-4 text-white/75 leading-relaxed">
                  The full hero artwork — chrome PSHL∞ insignia, a skate-hockey player in motion, the 8 PSHL pillars
                  (Speed, Skill, Strength, Strategy, Stamina, Spirit, Showmanship, Success), and the league tagline.
                  Built for broadcast title cards, on-arena LED, press decks, jersey-launch posters and large-format prints.
                </p>
                <a
                  href="/pshl8-hero-poster.png"
                  download
                  data-testid="brand-hero-poster-download"
                  className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:brightness-110 transition-all"
                >
                  <Download className="h-3.5 w-3.5" /> Download Hero Poster
                </a>
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2">
                <img
                  src="/pshl8-hero-poster.png"
                  alt="PSHL∞ broadcast hero poster — player, infinity insignia and 8 pillar tiles"
                  draggable={false}
                  className="w-full h-auto select-none border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.65)]"
                />
              </div>
            </div>
          </div>

          {/* Master brand mark — the clean PSHL∞ + POWER/SPEED/HUSTLE/LEGACY identity */}
          <div className="grid lg:grid-cols-12 gap-8 items-center bg-pshl-surface border border-pshl-gold/30 p-6 md:p-10 overflow-hidden relative">
            <div className="absolute -inset-16 bg-gradient-to-br from-pshl-electric/15 via-transparent to-pshl-gold/10 blur-3xl pointer-events-none" />
            <div className="relative lg:col-span-5 order-2 lg:order-1">
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-gold font-display mb-3">Master Brand Mark</div>
              <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter leading-[0.95]">
                The Official <span className="text-gold-metallic">PSHL∞</span> Identity.
              </h2>
              <p className="mt-4 text-white/75 leading-relaxed">
                Chrome-silver PSHL with a metallic-gold ∞ that doubles as the figure 8. Four brand pillars locked-in:
                <span className="text-pshl-red font-semibold"> Power</span>,
                <span className="text-pshl-electric font-semibold"> Speed</span>,
                <span className="text-[#FFB347] font-semibold"> Hustle</span>,
                <span className="text-white/85 font-semibold"> Legacy</span>. Tagline: Infinite Performance.
              </p>
              <a
                href="/pshl8-brand.png"
                download
                data-testid="master-brand-download"
                className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:brightness-110 transition-all"
              >
                <Download className="h-3.5 w-3.5" /> Download Master PNG
              </a>
            </div>
            <div className="relative lg:col-span-7 order-1 lg:order-2">
              <img
                src="/pshl8-brand.png"
                alt="PSHL∞ official master brand mark"
                draggable={false}
                className="w-full h-auto select-none border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BRAND PACK */}
      <section data-testid="section-brand-pack" className="section pt-0">
        <div className="pshl-container">
          <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Brand Pack</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter leading-[0.95]">
                The PSHL<span className="text-gold-metallic">∞</span> Logo Pack.
              </h2>
              <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
                Three official wordmark variants, supplied as scalable SVG. Pick the right variant for your
                background and use case — broadcast, print, merchandise or single-color application.
              </p>
            </div>
            <a
              href="/pshl8-brand.png"
              download
              data-testid="brand-pack-download-poster"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 transition-all"
            >
              <Download className="h-3.5 w-3.5" /> Full Brand Poster
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {VARIANTS.map((v) => (
              <div
                key={v.id}
                data-testid={`brand-variant-${v.id}`}
                className="bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors flex flex-col overflow-hidden"
              >
                {/* Preview swatch */}
                <div className={`relative ${v.bgClass} px-6 py-12 flex items-center justify-center min-h-[180px]`}>
                  {v.component}
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] font-display font-semibold bg-black/60 backdrop-blur-sm text-white/90 border border-white/15 px-2 py-1">
                    {v.swatch} BG
                  </span>
                </div>
                {/* Meta */}
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric font-display">Variant</div>
                  <div className="font-display text-xl text-white mt-1">{v.label}</div>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed flex-1">{v.desc}</p>
                  <div className="mt-5 flex items-center gap-2">
                    <a
                      href={v.file}
                      download
                      data-testid={`brand-variant-download-${v.id}`}
                      className="inline-flex items-center gap-2 bg-pshl-electric hover:bg-pshl-electric/90 text-white font-display uppercase tracking-[0.18em] text-xs px-4 py-2.5"
                    >
                      <Download className="h-3.5 w-3.5" /> SVG
                    </a>
                    <a
                      href={v.file}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`brand-variant-view-${v.id}`}
                      className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-4 py-2.5"
                    >
                      View
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Social Avatars — Instagram / X / LinkedIn / Facebook profile picture set */}
          <div data-testid="brand-pack-social-avatars" className="mt-10 bg-pshl-surface border border-pshl-gold/30 p-6 md:p-8 overflow-hidden relative">
            <div className="absolute -inset-16 bg-gradient-to-br from-pshl-gold/12 via-transparent to-pshl-electric/10 blur-3xl pointer-events-none" />
            <div className="relative flex items-end justify-between mb-6 gap-5 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-pshl-gold font-display mb-2">Social Avatars</div>
                <h3 className="font-display text-2xl md:text-3xl text-white tracking-tighter leading-[1.05]">
                  PSHL<span className="text-gold-metallic">∞</span> Profile Picture Pack.
                </h3>
                <p className="mt-3 text-sm text-white/70 max-w-2xl leading-relaxed">
                  Square gold-∞ profile pictures for Instagram, X, LinkedIn and Facebook handles. Three sizes
                  matched to platform spec — pick the size your destination requires.
                </p>
              </div>
            </div>
            <div className="relative grid sm:grid-cols-3 gap-5">
              {[
                { size: 1024, file: "/pshl8-avatar-1024.png", label: "Master",       sub: "Upload anywhere — auto-downscales", testid: "avatar-1024" },
                { size: 400,  file: "/pshl8-avatar-400.png",  label: "X · LinkedIn", sub: "400×400 spec — Twitter/X + LinkedIn page logo", testid: "avatar-400" },
                { size: 320,  file: "/pshl8-avatar-320.png",  label: "Instagram · Facebook", sub: "320×320 spec — Instagram + Facebook handle", testid: "avatar-320" },
              ].map((a) => (
                <div
                  key={a.size}
                  data-testid={a.testid}
                  className="border border-white/10 hover:border-pshl-gold/60 transition-colors bg-black/40 flex flex-col"
                >
                  <div className="aspect-square bg-[#020A1A] grid place-items-center p-4">
                    <img
                      src={a.file}
                      alt={`PSHL∞ avatar ${a.size}px`}
                      draggable={false}
                      className="max-w-[180px] w-full h-auto rounded-full select-none shadow-[0_0_36px_rgba(255,215,0,0.20)]"
                    />
                  </div>
                  <div className="p-4 flex flex-col flex-1 border-t border-white/8">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-display text-base text-white uppercase tracking-[0.12em]">{a.label}</div>
                      <span className="font-mono text-[10px] text-pshl-gold bg-pshl-gold/10 border border-pshl-gold/30 px-2 py-0.5">
                        {a.size}×{a.size}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-white/55 leading-relaxed flex-1">{a.sub}</p>
                    <div className="mt-4 flex items-center gap-2">
                      <a
                        href={a.file}
                        download
                        data-testid={`${a.testid}-download`}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-4 py-2.5 hover:brightness-110 transition-all"
                      >
                        <Download className="h-3.5 w-3.5" /> PNG
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          const url = `${window.location.origin}${a.file}`;
                          try {
                            await navigator.clipboard.writeText(url);
                            toast.success(`Copied ${a.size}px avatar URL`, { description: url });
                          } catch {
                            toast.error("Couldn't copy — long-press to copy manually");
                          }
                        }}
                        data-testid={`${a.testid}-copy-link`}
                        title="Copy direct CDN link"
                        className="inline-flex items-center justify-center gap-1.5 border border-pshl-gold/40 hover:border-pshl-gold text-pshl-gold hover:bg-pshl-gold/10 font-display uppercase tracking-[0.18em] text-xs px-3 py-2.5 transition-colors"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        <span className="hidden md:inline">Link</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Usage rules */}
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { t: "Clear Space", d: "Always leave at least the height of the ∞ symbol around the wordmark." },
              { t: "Minimum Size", d: "Don't render the wordmark below 24px height. Use the monochrome variant for very small placements." },
              { t: "Do Not", d: "Recolor PSHL, distort proportions, add drop shadows, or place the primary variant on light backgrounds." },
            ].map((r) => (
              <div key={r.t} className="bg-pshl-surface border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric font-display">Rule</div>
                <div className="font-display text-lg text-white mt-2">{r.t}</div>
                <div className="text-sm text-white/65 mt-2 leading-relaxed">{r.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER ASSETS */}
      <section className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Newsroom Assets</div>
          <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter mb-8">Press, Highlights, Gallery.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ASSETS.map(({ Icon, title, desc }, i) => (
              <div key={title} data-testid={`media-asset-${i}`} className="bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors p-6 flex flex-col">
                <div className="h-10 w-10 grid place-items-center bg-pshl-electric/15 border border-pshl-electric/40">
                  <Icon className="h-5 w-5 text-pshl-electric" />
                </div>
                <div className="font-display text-xl text-white mt-4">{title}</div>
                <div className="text-sm text-white/65 mt-2 leading-relaxed flex-1">{desc}</div>
                <a href="#" className="mt-5 inline-flex items-center gap-2 text-xs font-display uppercase tracking-[0.18em] text-pshl-electric hover:text-white">
                  <Download className="h-3.5 w-3.5" /> Request Access
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEDIA INQUIRY */}
      <section className="section">
        <div className="pshl-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Media Inquiry</div>
            <h2 className="font-display text-4xl text-white tracking-tighter">Cover PSHL8.</h2>
            <p className="mt-4 text-white/75 leading-relaxed">For interviews, accreditations, or co-produced content — send us a brief.</p>
          </div>
          <div className="lg:col-span-7"><LeadForm defaultInterest="media" title="Media Inquiry" /></div>
        </div>
      </section>
    </div>
  );
}
